/**
 * Features Pinia Store
 *
 * Orchestrates MVI: intents <-> reducer <-> effects.
 */

import { ref, computed } from "vue";
import { defineStore } from "pinia";
import type {
  ICreateFeatureRequest,
  IUpdateFeatureRequest,
  TFeatureId,
} from "@/shared/types";
import type { IFeaturesState } from "./state";
import {
  createInitialState,
  selectFeatures,
  selectProjectId,
  selectFeaturesWithArchived,
  selectSelectorOptions,
  selectSelectedFeature,
  selectSelectedFeatureId,
  selectIsLoading,
  selectIsCreating,
  selectIsSaving,
  selectIsDeleting,
  selectError,
} from "./state";
import type { TFeaturesIntent } from "./intents";
import { intents } from "./intents";
import { featuresReducer } from "./reducer";
import * as effects from "./effects";

export const useFeaturesStore = defineStore("features", () => {
  // ========================================================================
  // State
  // ========================================================================

  const state = ref<IFeaturesState>(createInitialState());

  // ========================================================================
  // Dispatch
  // ========================================================================

  function dispatch(intent: TFeaturesIntent): void {
    state.value = featuresReducer(state.value, intent);
  }

  // ========================================================================
  // Computed (Selectors)
  // ========================================================================

  const features = computed(() => selectFeatures(state.value));
  /** Проект загруженного перечня (R10). */
  const projectId = computed(() => selectProjectId(state.value));
  const featuresWithArchived = computed(() =>
    selectFeaturesWithArchived(state.value),
  );
  const selectorOptions = computed(() => selectSelectorOptions(state.value));
  const selectedFeature = computed(() => selectSelectedFeature(state.value));
  const selectedFeatureId = computed(() => selectSelectedFeatureId(state.value));
  const isLoading = computed(() => selectIsLoading(state.value));
  const isCreating = computed(() => selectIsCreating(state.value));
  const isSaving = computed(() => selectIsSaving(state.value));
  const isDeleting = computed(() => selectIsDeleting(state.value));
  const error = computed(() => selectError(state.value));

  // ========================================================================
  // Actions
  // ========================================================================

  async function loadFeatures(projectId: string): Promise<void> {
    await effects.loadFeatures(dispatch, projectId);
  }

  /** Перечень с архивными — только для поверхности управления фичами (R28). */
  async function loadFeaturesWithArchived(projectId: string): Promise<void> {
    await effects.loadFeaturesWithArchived(dispatch, projectId);
  }

  function selectFeature(featureId: string): void {
    dispatch(intents.selectFeature(featureId));
  }

  async function createFeature(
    projectId: string,
    request: ICreateFeatureRequest,
  ): Promise<void> {
    await effects.createFeature(dispatch, projectId, request);
  }

  async function updateFeature(
    projectId: string,
    featureId: TFeatureId,
    request: IUpdateFeatureRequest,
  ): Promise<void> {
    await effects.updateFeature(dispatch, projectId, featureId, request);
  }

  async function deleteFeature(
    projectId: string,
    featureId: TFeatureId,
  ): Promise<void> {
    await effects.deleteFeature(dispatch, projectId, featureId);
  }

  function clearFeatures(): void {
    dispatch(intents.clearFeatures());
  }

  // ========================================================================
  // Return
  // ========================================================================

  return {
    // State (readonly)
    state: computed(() => state.value),

    // Computed
    features,
    projectId,
    featuresWithArchived,
    selectorOptions,
    selectedFeature,
    selectedFeatureId,
    isLoading,
    isCreating,
    isSaving,
    isDeleting,
    error,

    // Actions
    loadFeatures,
    loadFeaturesWithArchived,
    selectFeature,
    createFeature,
    updateFeature,
    deleteFeature,
    clearFeatures,
  };
});
